/**
 * Small pieces every Verify tab uses: labels, status badges, form fields and the
 * 80-hour progress bar. Labels live here once so the five tabs and the member
 * page cannot drift on what "confirmed" or "self-reported" is called.
 */

import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { formatDateOnly } from '../../utils/dateTime';
import type { RecordSource, RecordStatus, StatementState } from './api';

type T = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

export function activityLabel(t: T, type: string | null | undefined): string {
  switch (type) {
    case 'employment':
      return t('verify.activity.employment', 'Paid work');
    case 'volunteer':
      return t('verify.activity.volunteer', 'Volunteering');
    case 'community_service':
      return t('verify.activity.community_service', 'Community service');
    case 'job_training':
      return t('verify.activity.job_training', 'Job training');
    case 'education':
      return t('verify.activity.education', 'School or classes');
    case 'job_search':
      return t('verify.activity.job_search', 'Job search');
    default:
      return type ? type.replace(/_/g, ' ') : '—';
  }
}

export function sourceLabel(t: T, source: RecordSource | string): string {
  switch (source) {
    case 'staff_entry':
      return t('verify.source.staff_entry', 'Staff entry');
    case 'qr_checkin':
      return t('verify.source.qr_checkin', 'QR check-in');
    case 'sheet_photo':
      return t('verify.source.sheet_photo', 'Paper sign-in sheet');
    case 'roster':
      return t('verify.source.roster', 'Roster');
    case 'member_self':
      return t('verify.source.member_self', 'Self-reported');
    default:
      return source;
  }
}

export function elementLabel(t: T, element: string): string {
  switch (element) {
    case 'qualifying_basis':
      return t('verify.element.qualifying_basis', 'Qualifying basis');
    case 'evidence':
      return t('verify.element.evidence', 'Supporting evidence');
    case 'oversight_method':
      return t('verify.element.oversight_method', 'Oversight method');
    case 'point_of_contact':
      return t('verify.element.point_of_contact', 'Point of contact');
    case 'activity_types':
      return t('verify.element.activity_types', 'Qualifying activity types');
    default:
      return element;
  }
}

export function missingFieldLabel(t: T, field: string): string {
  switch (field) {
    case 'type_of_service':
      return t('verify.missing.type_of_service', 'No type of service');
    case 'date':
      return t('verify.missing.date', 'No date');
    case 'hours':
      return t('verify.missing.hours', 'No hours');
    case 'point_of_contact':
      return t('verify.missing.point_of_contact', 'No point of contact');
    default:
      return field;
  }
}

export const humanize = (value: string | null | undefined) =>
  value ? value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : '—';

export function RecordStatusBadge({ status, t }: { status: RecordStatus; t: T }) {
  const map: Record<RecordStatus, { variant: 'success' | 'warning' | 'danger' | 'muted' | 'info'; label: string }> = {
    confirmed: { variant: 'success', label: t('verify.status.confirmed', 'Confirmed') },
    self_attested: { variant: 'info', label: t('verify.status.self_attested', 'Self-reported') },
    captured: { variant: 'warning', label: t('verify.status.captured', 'Awaiting confirmation') },
    disputed: { variant: 'danger', label: t('verify.status.disputed', 'Disputed') },
    voided: { variant: 'muted', label: t('verify.status.voided', 'Voided') },
  };
  const m = map[status] ?? { variant: 'muted' as const, label: status };
  return (
    <Badge variant={m.variant} size="sm">
      {m.label}
    </Badge>
  );
}

export function statementStateLabel(t: T, state: StatementState): string {
  switch (state) {
    case 'complete':
      return t('verify.state.complete', 'Complete');
    case 'meets_needs_evidence':
      return t('verify.state.meets_needs_evidence', '80 h met, evidence incomplete');
    case 'awaiting_confirmation':
      return t('verify.state.awaiting_confirmation', 'Awaiting confirmation');
    case 'short_at_deadline':
      return t('verify.state.short_at_deadline', 'Short at deadline');
    case 'at_risk':
      return t('verify.state.at_risk', 'At risk');
    default:
      return t('verify.state.in_progress', 'In progress');
  }
}

export function StatementStateBadge({ state, t }: { state: StatementState; t: T }) {
  const variant =
    state === 'complete'
      ? 'success'
      : state === 'short_at_deadline'
        ? 'danger'
        : state === 'at_risk' || state === 'meets_needs_evidence' || state === 'awaiting_confirmation'
          ? 'warning'
          : 'muted';
  return (
    <Badge variant={variant} size="sm">
      {statementStateLabel(t, state)}
    </Badge>
  );
}

export function SiteStatusBadge({ status, t }: { status: string; t: T }) {
  const variant = status === 'qualified' ? 'success' : status === 'suspended' ? 'danger' : 'warning';
  const label =
    status === 'qualified'
      ? t('verify.site_status.qualified', 'Qualified')
      : status === 'in_review'
        ? t('verify.site_status.in_review', 'In review')
        : status === 'suspended'
          ? t('verify.site_status.suspended', 'Suspended')
          : t('verify.site_status.invited', 'Invited');
  return (
    <Badge variant={variant} size="sm">
      {label}
    </Badge>
  );
}

/** Counted hours against the 80-hour monthly standard. */
export function HoursBar({
  counted,
  verified,
  required = 80,
  pending = 0,
}: {
  counted: number;
  verified: number;
  required?: number;
  pending?: number;
}) {
  const pct = (v: number) => `${Math.min(100, (v / required) * 100)}%`;
  const selfReported = Math.max(0, counted - verified);
  return (
    <div className="h-3 w-full overflow-hidden rounded-full bg-paper-2" aria-hidden>
      <div className="flex h-full">
        <div className="h-full bg-v2accent" style={{ width: pct(verified) }} />
        <div className="h-full bg-v2accent-soft" style={{ width: pct(selfReported) }} />
        <div
          className="h-full bg-attention-soft"
          style={{ width: pct(Math.min(pending, Math.max(0, required - counted))) }}
        />
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
  className = '',
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block space-y-1 ${className}`}>
      <span className="text-[13px] font-medium text-ink-700">{label}</span>
      {children}
      {hint ? <span className="block text-xs text-ink-500">{hint}</span> : null}
    </label>
  );
}

export const inputClass =
  'w-full rounded-md border border-v2border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-v2accent';

export function NativeSelect(
  props: React.SelectHTMLAttributes<HTMLSelectElement> & { children: React.ReactNode },
) {
  const { className = '', ...rest } = props;
  return <select {...rest} className={`${inputClass} ${className}`} />;
}

export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-v2border bg-danger-soft px-3 py-2 text-sm text-ink-700">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
      <span>{message}</span>
    </div>
  );
}

export const fmtDate = (value: string | null | undefined) => (value ? formatDateOnly(value) : '—');

export const fmtHours = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} h`;

export const monthName = (year: number, month: number, locale?: string) =>
  new Date(year, month - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

export const shortMonth = (year: number, month: number, locale?: string) =>
  new Date(year, month - 1, 1).toLocaleDateString(locale, { month: 'short' });

export const errMessage = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
